import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import "../styles/consulting.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import Service from "../components/Services";
import "../styles/LandscapeCard.css";
import "../styles/sliderzx.css";
import "../styles/EinvCarousel.css";
import "../styles/home-theme.css";
import HowWeHelp from "../components/HowWeHelp";
import StatBar from "../components/StatBar";
import CardIllustration from "../components/CardIllustration";
import EinvImg from "../assests/images/Einv.jpg";
import PeppolImg from "../assests/images/peppol1.png";
import impactImg from "../assests/images/impact.jpg";
import ComplyIMG from "../assests/images/Comply.webp";
import CloudareForEinvImg from "../assests/images/CloudareForEinvCard.jpg";
import {
  PRODUCTS_OPTION,
  PRODUCT_FIRST_SLIDER,
  PRODUCT_SECOND_SLIDER,
  INDIA_FINANCE,
  CONSULT_SERVICES,
} from "../constant";
import Partners from "../components/Partners";
import ProductLanding from "../components/productLanding";
import Landing from "../components/Landing";
import project from "../assests/images/GotAProject.jpg";
import EventPopup from "../components/EventPopup";
import ERP from "../assests/images/ERP1.jpg";
import ProductCardPanel from "../components/ProductCardPanel";
import WhatSetsUsApart from "../components/WhatSetsUsApart";
import ServicesGrid from "../components/ServicesGrid";
import Seo from "../components/Seo";

function Consultation() {
  const navigate = useNavigate();

  // Card data array with navigation routes
  const einvCards = useMemo(
    () => [
      {
        id: "einv",
        img: EinvImg,
        title: "What is E-Invoicing?",
        description:
          "Learn about electronic invoicing and how it improves efficiency.",
        icon: "📄",
        category: "Fundamentals",
        route: "/einv",
        readTime: "5 min",
      },
      {
        id: "peppol",
        img: PeppolImg,
        title: "What is PEPPOL?",
        description:
          "Discover how PEPPOL enables secure cross-border e-invoicing.",
        icon: "🌐",
        category: "Network",
        route: "/peppol",
        readTime: "4 min",
      },
      {
        id: "impact",
        img: impactImg,
        title: "How E-Invoicing impacts business?",
        description:
          "Understand the benefits of e-invoicing on compliance and growth.",
        icon: "📈",
        category: "Business",
        route: "/ImpactOfEinv",
        readTime: "6 min",
      },
      {
        id: "comply",
        img: ComplyIMG,
        title: "How to Comply with New Regulations?",
        description: "Step-by-step guide to E-Invoicing compliance.",
        icon: "✅",
        category: "Compliance",
        route: "/HowToComply",
        readTime: "8 min",
      },
      {
        id: "cloudare",
        img: CloudareForEinvImg,
        title: "How Cloudare Helps with E-Invoicing?",
        description:
          "Discover how Cloudare's ERP solutions streamline compliance with automation.",
        icon: "☁️",
        category: "Solutions",
        route: "/CloudareForEinv",
        readTime: "5 min",
      },
    ],
    []
  );

  const totalCards = einvCards.length;

  // Calculate visible cards based on screen size
  const getCardsPerView = useCallback(() => {
    if (typeof window !== "undefined") {
      if (window.innerWidth <= 480) return 1;
      if (window.innerWidth <= 768) return 2;
      if (window.innerWidth <= 1024) return 3;
      return 3;
    }
    return 3;
  }, []);

  const [cardsPerView, setCardsPerView] = useState(getCardsPerView());

  // Create extended cards for infinite loop (triple the array)
  const extendedCards = useMemo(() => {
    return [...einvCards, ...einvCards, ...einvCards];
  }, [einvCards]);

  // Carousel States - start from middle set for seamless backwards scrolling
  const [currentIndex, setCurrentIndex] = useState(totalCards);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Real index for dot indicators (0 to totalCards-1)
  const realIndex = ((currentIndex % totalCards) + totalCards) % totalCards;

  // Auto-play functionality
  useEffect(() => {
    if (!isDragging && !isPaused) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 4000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isDragging, isPaused]);

  // Handle seamless infinite loop reset
  useEffect(() => {
    // If we've scrolled too far right (into third set)
    if (currentIndex >= totalCards * 2) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(currentIndex - totalCards);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }, 500);
      return () => clearTimeout(timer);
    }

    // If we've scrolled too far left (before first set)
    if (currentIndex < 0) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(currentIndex + totalCards);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, totalCards]);

  // Navigation functions
  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => prev + 1);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => prev - 1);
  }, []);

  const goToSlide = useCallback(
    (index) => {
      // Calculate the target index in the current set
      const currentSet = Math.floor(currentIndex / totalCards);
      const targetIndex = currentSet * totalCards + index;

      // Find the shortest path to the target
      const diff = targetIndex - currentIndex;
      if (Math.abs(diff) <= totalCards / 2) {
        setCurrentIndex(targetIndex);
      } else if (diff > 0) {
        setCurrentIndex(targetIndex - totalCards);
      } else {
        setCurrentIndex(targetIndex + totalCards);
      }
    },
    [currentIndex, totalCards]
  );

  // Handle card click - Navigate to route
  const handleCardClick = (route) => {
    navigate(route);
  };

  // Handle CTA button click with event propagation stop
  const handleCtaClick = (e, route) => {
    e.stopPropagation();
    navigate(route);
  };

  // Touch/Drag handlers
  const handleDragStart = (e) => {
    setIsDragging(true);
    setStartX(e.type === "touchstart" ? e.touches[0].clientX : e.clientX);
    setIsPaused(true);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const currentX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
    const diff = currentX - startX;
    setTranslateX(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (translateX > 50) {
      prevSlide();
    } else if (translateX < -50) {
      nextSlide();
    }

    setTranslateX(0);
    setTimeout(() => setIsPaused(false), 3000);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        prevSlide();
      } else if (e.key === "ArrowRight") {
        nextSlide();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextSlide, prevSlide]);

  // Responsive handler
  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(getCardsPerView());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getCardsPerView]);

  // Calculate transform value
  const getTransformValue = () => {
    const cardWidth = 100 / cardsPerView;
    const baseTransform = currentIndex * cardWidth;
    return `translateX(calc(-${baseTransform}% + ${translateX}px))`;
  };

  return (
    <React.Fragment>
        <Seo path="/" />
      <div className="layout">
        <div className="contact">
          <div className="call-detail"></div>
        </div>
        <Navbar />
        <EventPopup />

        <ProductLanding
          title="Simplifying ERP."
          subtitle="Automating E-Invoicing. Enabling Growth."
          content={
            <>
              <p>
                Cloudare Technologies helps businesses implement, integrate,
                customize, and scale leading ERP platforms—including Oracle
                (NetSuite, Fusion, EBS, Opera), Odoo, Zoho, MS Dynamics, Sage,
                Tally and QuickBooks—ensuring seamless adoption, optimized
                workflows, and future-ready digital transformation across global
                operations.
              </p>

              <p>
                Our e-invoicing and compliance practice delivers smart
                integrations, accurate data mapping and customized dashboards,
                keeping all your data within your ERP for automated, audit-ready
                operations.
              </p>

              <p>
                We bridge technology, compliance, and growth—so enterprises can
                focus on what matters most:{" "}
                <strong>
                  driving innovation, serving customers, and scaling with
                  confidence.
                </strong>
              </p>
            </>
          }
          label="Contact Us"
          trigger={true}
          src={ERP}
          id={2}
        />

        {/* ============================================
            Premium E-Invoicing Carousel Section
            ============================================ */}
        <section className="einv-carousel-section">
          {/* Section Header */}
          <div className="einv-carousel-header">
            <div className="einv-header-decoration">
              <span className="decoration-line"></span>
              <span className="decoration-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2V8H20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="decoration-line"></span>
            </div>
            <span className="einv-carousel-badge">Knowledge Hub</span>
            <h2 className="einv-carousel-title">
              E-Invoicing & Compliance Solutions
            </h2>
            <p className="einv-carousel-subtitle">
              Explore our comprehensive guides to streamline your digital
              transformation journey
            </p>
          </div>

          {/* Carousel Container */}
          <div
            className="einv-carousel-wrapper"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Navigation Arrow - Left */}
            <button
              className="einv-carousel-nav einv-carousel-nav-left"
              onClick={prevSlide}
              aria-label="Previous slide"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Carousel Track */}
            <div
              className="einv-carousel-container"
              ref={carouselRef}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={handleDragEnd}
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
            >
              <div
                className="einv-carousel-track"
                style={{
                  transform: getTransformValue(),
                  transition:
                    isDragging || !isTransitioning
                      ? "none"
                      : "transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)",
                }}
              >
                {extendedCards.map((card, index) => (
                  <div
                    key={`${card.id}-${index}`}
                    className={`einv-carousel-card ${
                      index === currentIndex ? "active" : ""
                    }`}
                    style={{ flex: `0 0 ${100 / cardsPerView}%` }}
                    onClick={() => handleCardClick(card.route)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleCardClick(card.route);
                      }
                    }}
                    aria-label={`Navigate to ${card.title}`}
                  >
                    <div className="einv-card-inner">
                      {/* Card Image Section */}
                      <div className="einv-card-image-wrapper">
                        <img src={card.img} alt={card.title} loading="lazy" />
                        <div className="einv-card-image-overlay">
                          <span className="einv-card-icon">{card.icon}</span>
                        </div>
                        <span className="einv-card-category">
                          {card.category}
                        </span>

                        {/* Learn More Overlay Text */}
                        <div className="einv-card-hover-text">
                          <span>Click to Learn More</span>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M5 12H19M19 12L12 5M19 12L12 19"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Card Content Section */}
                      <div className="einv-card-content">
                        <h3 className="einv-card-title">{card.title}</h3>
                        <p className="einv-card-description">
                          {card.description}
                        </p>

                        {/* Card Footer */}
                        <div className="einv-card-footer">
                          <button
                            className="einv-card-cta"
                            onClick={(e) => handleCtaClick(e, card.route)}
                          >
                            <span>Learn More</span>
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M5 12H19M19 12L12 5M19 12L12 19"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          {/* <span className="einv-card-read-time">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <path
                                d="M12 6V12L16 14"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                            {card.readTime} read
                          </span> */}
                        </div>
                      </div>

                      {/* Hover Effect Border */}
                      <div className="einv-card-border-effect"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrow - Right */}
            <button
              className="einv-carousel-nav einv-carousel-nav-right"
              onClick={nextSlide}
              aria-label="Next slide"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Carousel Controls - Dots Only */}
          <div className="einv-carousel-controls">
            {/* Premium Dot Indicators */}
            <div className="einv-carousel-indicators">
              {einvCards.map((_, index) => (
                <button
                  key={index}
                  className={`einv-carousel-dot ${
                    index === realIndex ? "active" : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <span className="dot-inner"></span>
                </button>
              ))}
            </div>

            {/* Subtle Progress Indicator */}
            <div className="einv-carousel-progress-wrapper">
              <div className="einv-carousel-progress">
                <div
                  className="einv-carousel-progress-bar"
                  style={{
                    width: `${((realIndex + 1) / totalCards) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Background Decorations */}
          <div className="einv-section-decoration einv-decoration-1"></div>
          <div className="einv-section-decoration einv-decoration-2"></div>
          <div className="einv-section-decoration einv-decoration-3"></div>
        </section>
        {/* End of Carousel Section */}
      </div>

      {/* Rest of the page content */}
      <section className="services" style={{ marginTop: "74px" }}>
        <ServicesGrid />

        <ProductCardPanel />

        <WhatSetsUsApart />

        <HowWeHelp />

        <div className="box" id="background" style={{ marginTop: "60px" }}>
          <Landing
            title="Empower your business with Cloudare"
            label="Let's Talk"
            content="Got a project?"
            href="https://calendly.com/cloudare"
            src={project}
          />
        </div>
        <Partners
          list={PRODUCT_FIRST_SLIDER}
          title="Enterprises and startups choose Cloudare as SI partner to scale their teams"
        />
        <Partners list={PRODUCT_SECOND_SLIDER} title="Our Clients" />
      </section>
      <StatBar />
      <section className="erp-partner-section">
        <h2 className="erp-heading">
          What to Look for in an ERP Implementation Partner?
        </h2>

        <div className="why-us">
          {INDIA_FINANCE.map((data, i) => {
            return (
              <div key={i} className="cardl">
                <CardIllustration name={data.art} className="card-imagel" />
                <div className="card-contentl">
                  <h3 className="h3l">{data.title}</h3>
                  <p>{data.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </React.Fragment>
  );
}

export default Consultation;
